import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import JSONataEditor from 'react-scale/corp/components/JSONataEditor';
import { useCompassCard } from '@scale/compass-ui/store';
import { CDSDownloadResponseContext } from '@scale/genai-compass-shared/cards/cds_download_response';
import { useState, useEffect } from 'react';
import { useDebounce } from 'react-use-v13';
import { Text, FormField, Callout, Code, Link, TextField } from '@scale/scaleui-radix';
import { faBookOpen } from '@fortawesome/pro-solid-svg-icons';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';

const CDSResponseDownloadCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard(index);
  const context = card.defaultContext as CDSDownloadResponseContext;

  // Local state for the editor and project ID
  const [localJsonata, setLocalJsonata] = useState(context.jsonataExpression);
  const [localProjectId, setLocalProjectId] = useState(context.projectId);

  // Debounced effect to update context
  useDebounce(
    () => {
      if (localJsonata !== context.jsonataExpression || localProjectId !== context.projectId) {
        updateCardContext({
          ...context,
          jsonataExpression: localJsonata,
          projectId: localProjectId,
        } as CDSDownloadResponseContext);
      }
    },
    200,
    [localJsonata, localProjectId],
  );
  // Sync local state when context changes from outside
  useEffect(() => {
    setLocalJsonata(context.jsonataExpression);
    setLocalProjectId(context.projectId);
  }, [context.jsonataExpression, context.projectId]);

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-3">
        <CompassInstructions
          header="How to use the CDS Response Download card"
          storageKey="cdsResponseDownloadCardInstructionsExpanded"
          icon={faBookOpen}
        >
          <Callout.Text>
            <Text as="p">
              <div className="mb-3">
                Use JSONata expressions to transform task attempt responses into your desired table
                format. The transformed data will be added as a new column. Expects column 'attempt'
              </div>

              <div className="mb-3">
                To test expressions, use the FILTER card before this card to limit to 1 row, then
                run this card with the <Code>$</Code> expression and test in the{' '}
                <Link href="https://try.jsonata.org/">JSONata Playground</Link>. You can also
                retrieve the full response and extract later using the jsons package in Python
                steps.
              </div>
            </Text>
          </Callout.Text>
        </CompassInstructions>
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <FormField.Root size="3">
          <FormField.Label htmlFor="project-id">Project ID</FormField.Label>
          <TextField.Root
            id="project-id"
            value={localProjectId}
            onChange={e => setLocalProjectId(e.target.value)}
            placeholder="Enter project ID"
          />
        </FormField.Root>
      </div>

      <div className="flex flex-col gap-2 mb-3">
        <FormField.Root size="3">
          <FormField.Label htmlFor="response-column-name">JSONata Expression</FormField.Label>
          <div className="h-[200px] border border-[#d9deec] rounded-sm">
            <JSONataEditor
              value={localJsonata}
              onChange={setLocalJsonata}
              options={{
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
              }}
            />
          </div>
        </FormField.Root>
      </div>
    </div>
  );
};

export default CDSResponseDownloadCardComponent;
